import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";

// ---------------------------------------------------------------------------
// Auto-recover from stale chunk loads.
//
// When we ship a new build, hashed chunk filenames change. Anyone with the
// old app open in a tab still holds the old `main.js`, which references
// chunks that no longer exist — attempting to lazy-load one throws a
// ChunkLoadError and the app dies with "Unexpected token '<'" (because the
// missing chunk URL returns the SPA's index.html, which starts with `<`).
//
// The fix is simple: catch it, mark that we've tried, and hard-refresh. The
// browser fetches the new `main.js` on reload and everything works. We
// guard with sessionStorage so we don't infinite-loop if the reload itself
// fails (offline, bad deploy).
// ---------------------------------------------------------------------------
const RELOAD_KEY = "mantle_chunk_reload_attempt";

function isChunkLoadError(err) {
  if (!err) return false;
  const msg = err.message || String(err);
  return err.name === "ChunkLoadError"
    || /Loading chunk .* failed/i.test(msg)
    || /Loading CSS chunk .* failed/i.test(msg)
    || /Failed to fetch dynamically imported module/i.test(msg)
    || /Unexpected token '<'/i.test(msg);
}

function recoverFromStaleBundle() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    // Only auto-reload once per 30 seconds — protects against loops
    // if the deploy itself is serving broken files.
    if (Date.now() - last < 30_000) return;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    window.location.reload();
  } catch {
    /* sessionStorage may throw in privacy mode — no-op */
  }
}

window.addEventListener("error", (e) => {
  if (isChunkLoadError(e.error || e)) recoverFromStaleBundle();
});
window.addEventListener("unhandledrejection", (e) => {
  if (isChunkLoadError(e.reason)) recoverFromStaleBundle();
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
